import { Box, styled } from "@mui/material";

import {
    PANEL_GAP,
    PANEL_WIDTH,
    sidePanelsActions,
    useDispatchSidePanels,
    useIsCollapsed,
    useOpenPanels,
} from "contexts/sidePanels";

import { SIDE_PANEL_LAYOUT, SIDE_PANEL_Z_INDEX } from "./constants";
import { SidePanel } from "./SidePanel";

const Container = styled(Box)`
    position: absolute;
    right: ${SIDE_PANEL_LAYOUT.RIGHT_MARGIN}px;
    top: ${SIDE_PANEL_LAYOUT.TOP_MARGIN}px;
    bottom: ${SIDE_PANEL_LAYOUT.BOTTOM_MARGIN}px;
    width: ${PANEL_WIDTH}px;
    display: flex;
    flex-direction: column;
    gap: ${PANEL_GAP}px;
    z-index: ${SIDE_PANEL_Z_INDEX};
    pointer-events: none;
`;

interface PanelWrapperProps {
    collapsed?: boolean;
}

const PanelWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== "collapsed",
})<PanelWrapperProps>`
    flex: ${({ collapsed }) => (collapsed ? "0 0 50%" : "1")};
    min-height: 0;
    display: flex;
    flex-direction: column;
`;

/**
 * Side Panel Container
 *
 * Container that renders open side panels in a vertical stack.
 * When 2 panels are open, they split the available height 50/50.
 * Panels can also be collapsed to half-height mode.
 *
 * This is similar to WindowManager but for docked panels.
 */
export function SidePanelContainer() {
    const openPanels = useOpenPanels();
    const dispatch = useDispatchSidePanels();
    const isCollapsed = useIsCollapsed();

    if (openPanels.length === 0) {
        return null;
    }

    const handleClosePanel = (panelId: string) => {
        dispatch(sidePanelsActions.closePanel(panelId));
    };

    const handleToggleCollapse = (panelId: string) => {
        dispatch(sidePanelsActions.toggleCollapsed(panelId));
    };

    // Collapsed mode: single panel at half height
    // Normal mode: panels share available space equally
    const shouldCollapse = isCollapsed && openPanels.length === 1;

    return (
        <Container>
            {openPanels.map((panel) => {
                const PanelComponent = panel.component;
                return (
                    <PanelWrapper key={panel.id} collapsed={shouldCollapse}>
                        <SidePanel
                            title={panel.title}
                            icon={panel.icon}
                            onClose={() => handleClosePanel(panel.id)}
                            onToggleCollapse={() => handleToggleCollapse(panel.id)}
                            isCollapsed={isCollapsed}
                            hideHeader={panel.hideHeader}
                            data-testid={`side-panel-${panel.id}`}
                        >
                            <PanelComponent onClose={() => handleClosePanel(panel.id)} />
                        </SidePanel>
                    </PanelWrapper>
                );
            })}
        </Container>
    );
}
